import { Button } from '@vadiun/react-components';
import { useSuperQuery } from '@vadiun/react-hooks-legacy';
import {
  BusinessUnitType,
  useBusinessUnit
} from 'app/business/services/useBusinessUnit';
import useAuthorizeds from 'app/client/clientBusiness/Authorized/services/AuthorizedService';
import { ClientBusinessPartialModel } from 'app/client/clientBusiness/models/ClientBusiness';
import {
  TIPOS_SERVICIO_VIAJE_TRASLADO,
  ViajeTrasladoTipoServicio,
  VIAJE_TRASLADO_ESTADOS,
  TipoServicioLabel
} from 'app/viaje/models';
import { Form, Formik, FormikProps } from 'formik';
import { Moment } from 'moment';
import { useEffect, useRef, useState } from 'react';
import FormikAutoSubmit from 'shared/components/FormikAutoSubmit';
import FormikOnFieldChange from 'shared/components/FormikOnFieldChange';
import FormField from 'shared/components/form/FormField';
import FormikCheckbox, {
  CheckboxField
} from 'shared/components/form/FormikCheckbox';
import FormikInput from 'shared/components/form/FormikInput';
import FormikSelect, {
  SelectOption
} from 'shared/components/form/FormikSelect';
import StyledAutocomplete from 'shared/components/StyledAutocomplete';
import StyledDateTimePicker from 'shared/components/StyledDateTimePicker';
import { useClientNames, useDriverNames } from 'shared/hooks';
import { tw } from 'utils/tailwindUtils';
import * as Yup from 'yup';
import { FilterCategoryTitle } from './FilterCategoryTitle';
import { BusType } from '../models/TransferReservation';

// Separador entre categorías de filtros (Periodo, Reserva, Pasajero...).
const sectionClass =
  'mb-4 border-b border-line pb-4 last:mb-0 last:border-b-0 dark:border-white/10';

export enum ColorState {
  RED = 'red',
  ORANGE = 'orange',
  YELLOW = 'yellow',
  GREEN = 'green',
  VIOLET = 'violet'
}

export type TransferReservationBaseFiltersFormType = {
  dateFrom: Moment | null;
  dateTo: Moment | null;
  clientId: number | null;
  serviceType: ViajeTrasladoTipoServicio | 'Todos';
  hasPriority: boolean;
  passangerName: string;
  street: string;
  locality?: string;
  phone: string;
  client_phone: string;
  client_type?:
    | 'BusinessClient'
    | 'ParticularClient'
    | 'ParticularApp'
    | 'AllClient';
  reservationId: string;
  oficial_cuenta_id?: null | number;
  authorized_id?: number | null;
  cost_center?: string;
  sub_cost_center?: string;
  buy_order?: string;
  sector?: string;
  driver_with_close_shift?: boolean;
  departure_time_exceeded?: boolean;
  assigned_driver_id?: number | null;
  pre_assigned_driver_id?: number | null;
  driverCode?: '';
  state?: string;
  plataformaId?: string;
  page?: number;
  page_size?: number;
  business_unit?: BusinessUnitType;
  sent_platform?: string;
  is_event: boolean;
  event_name?: string;
  is_llt_pais: boolean;
  from_app?: boolean;
  sent_reservations_platform: boolean;
  not_sent_reservations_platform: boolean;
  has_platform_id: boolean;
  bus_type_id?: number[];
  color?: ColorState;
  search?: string;
};

export const ViajeTrasladoBaseFiltersSchema = Yup.object({
  dateFrom: Yup.mixed(),
  dateTo: Yup.mixed(),
  clientId: Yup.number().nullable(),
  serviceType: Yup.string(),
  hasPriority: Yup.boolean(),
  passangerName: Yup.string(),
  street: Yup.string(),
  locality: Yup.string(),
  phone: Yup.string(),
  client_phone: Yup.string(),
  reservationId: Yup.string(),
  cost_center: Yup.string(),
  sub_cost_center: Yup.string(),
  buy_order: Yup.string(),
  sector: Yup.string(),
  driver_with_close_shift: Yup.boolean(),
  departure_time_exceeded: Yup.boolean(),
  business_unit: Yup.mixed(),
  sent_reservations_platform: Yup.boolean(),
  not_sent_reservations_platform: Yup.boolean(),
  has_platform_id: Yup.boolean(),
  is_event: Yup.boolean(),
  is_llt_pais: Yup.boolean(),
  from_app: Yup.boolean(),
  event_name: Yup.string(),
  bus_type_id: Yup.array(Yup.number()).nullable()
});

interface Props {
  onSubmit: (values: TransferReservationBaseFiltersFormType) => Promise<void>;
  initialValues: TransferReservationBaseFiltersFormType;
  filterByClient?: boolean;
  filterByAssignedDriver?: boolean;
  filterByPreAssignedDriver?: boolean;
  optionsBuses: BusType[];
  // Date filters
  filterByDateRange?: boolean;
  // Reserva filters
  filterByClientType?: boolean;
  filterByServiceType?: boolean;
  filterByBusType?: boolean;
  filterByStreet?: boolean;
  filterByLocality?: boolean;
  filterByReservationId?: boolean;
  filterByClientPhone?: boolean;
  filterByPriority?: boolean;
  filterByHasPlatformId?: boolean;
  filterByIsLltPais?: boolean;
  filterByFromApp?: boolean;
  filterByIsEvent?: boolean;
  filterByEventName?: boolean;
  filterBySentPlatform?: boolean;
  // Requerimientos filters
  filterByAuthorized?: boolean;
  filterByCostCenter?: boolean;
  filterBySubCostCenter?: boolean;
  filterBySector?: boolean;
  filterByBuyOrder?: boolean;
  filterByPlataformaId?: boolean;
  filterByState?: boolean;
  // Pasajero filters
  filterByPassangerName?: boolean;
  filterByPhone?: boolean;
  // Chofer filters
  filterByBusinessUnit?: boolean;
  filterByDriverWithCloseShift?: boolean;
  filterByDepartureTimeExceeded?: boolean;
  filterByViolet?: boolean;
  /** Aplica los filtros a medida que cambian (con debounce), sin botón "Buscar". */
  autoApply?: boolean;
}

const ViajeTrasladoBaseFilters = ({
  onSubmit,
  initialValues,
  filterByClient = true,
  filterByAssignedDriver = true,
  filterByPreAssignedDriver = true,
  optionsBuses,
  // Date filters
  filterByDateRange = true,
  // Reserva filters
  filterByClientType = true,
  filterByServiceType = true,
  filterByBusType = true,
  filterByStreet = true,
  filterByLocality = true,
  filterByReservationId = true,
  filterByClientPhone = true,
  filterByPriority = true,
  filterByHasPlatformId = true,
  filterByIsLltPais = true,
  filterByFromApp = true,
  filterByIsEvent = true,
  filterByEventName = true,
  filterBySentPlatform = true,
  // Requerimientos filters
  filterByAuthorized = true,
  filterByCostCenter = true,
  filterBySubCostCenter = true,
  filterBySector = true,
  filterByBuyOrder = true,
  filterByPlataformaId = true,
  filterByState = true,
  // Pasajero filters
  filterByPassangerName = true,
  filterByPhone = true,
  // Chofer filters
  filterByBusinessUnit = true,
  filterByDriverWithCloseShift = true,
  filterByDepartureTimeExceeded = true,
  filterByViolet = false,
  autoApply = false
}: Props) => {
  const authorizadosService = useAuthorizeds();
  const BusinessUnit = useBusinessUnit();

  const [clientIdSelected, setClientIdSelected] = useState<number | undefined>(
    undefined
  );

  const [disabledBuses, setDisabledBuses] = useState<boolean>(true);

  // Hooks reutilizables para obtener nombres de clientes y choferes
  const { clientesQuery, clientprivatesQuery, clientprivatesAppQuery } =
    useClientNames(filterByClient);
  const { driversQuery, getDriverName } = useDriverNames();
  const businessQuery = useSuperQuery(BusinessUnit.getBusinessUnit);

  const autorizadosQuery = useSuperQuery(
    async () => {
      return clientIdSelected
        ? await authorizadosService.getAuthorizeds(clientIdSelected)
        : [];
    },
    undefined,
    [clientIdSelected]
  );

  const formRef =
    useRef<FormikProps<TransferReservationBaseFiltersFormType>>(null);
  const prevClientRef = useRef('');

  useEffect(() => {
    prevClientRef.current = formRef.current?.values.client_type!;
  }, [formRef.current?.values.client_type]);

  useEffect(() => {
    if (prevClientRef.current !== formRef.current?.values.client_type!) {
      formRef.current?.setValues({
        ...formRef.current?.values,
        clientId: null
      });
    }
  }, [formRef.current?.values.client_type]);

  const getClientOptions = (clientType?: string) =>
    clientType === 'ParticularClient'
      ? (clientprivatesQuery.data ?? []).map((x) => x.id)
      : clientType === 'ParticularApp'
      ? (clientprivatesAppQuery.data ?? []).map((x) => x.id)
      : (clientesQuery.data ?? []).map((x) => x.id);

  const getClientLabel = (id: number, clientType?: string) => {
    if (clientType === 'ParticularClient' || clientType === 'ParticularApp') {
      const source =
        clientType === 'ParticularClient'
          ? clientprivatesQuery.data
          : clientprivatesAppQuery.data;
      const client = (source ?? []).find((x) => x.id === id);
      return [client?.name, client?.lastName].filter(Boolean).join(' ');
    }
    return (
      (clientesQuery.data ?? []).find((x) => x.id === id)?.fantasyName ?? ''
    );
  };

  const clientTypeOptions: SelectOption<string>[] = [
    { value: 'BusinessClient', label: 'Empresa' },
    { value: 'ParticularClient', label: 'Particular' },
    { value: 'ParticularApp', label: 'Particular App' },
    { value: 'AllClient', label: 'Todos' }
  ];

  const serviceTypeOptions: SelectOption<string>[] = [
    ...TIPOS_SERVICIO_VIAJE_TRASLADO.map((canal) => ({
      value: canal as string,
      label: TipoServicioLabel[`${canal}`]
    })),
    { value: 'Todos', label: 'Todos' }
  ];

  const stateOptions: SelectOption<string>[] = [
    ...VIAJE_TRASLADO_ESTADOS.map((state) => ({ value: state, label: state })),
    { value: 'Todos', label: 'Todos' }
  ];

  const authorizedOptions: SelectOption<number | undefined>[] = [
    { value: undefined, label: 'Todos' },
    ...(autorizadosQuery.data ?? []).map((authorized) => ({
      value: authorized.id as number | undefined,
      label: authorized.name
    }))
  ];

  return (
    <>
      <Formik<TransferReservationBaseFiltersFormType>
        initialValues={initialValues}
        innerRef={formRef}
        enableReinitialize
        validationSchema={ViajeTrasladoBaseFiltersSchema}
        onSubmit={onSubmit}
      >
        {({ submitForm, touched, errors, setFieldValue, ...formik }) => (
          <Form className="flex w-full flex-col">
            {filterByViolet && (
              <div className={sectionClass}>
                <CheckboxField
                  label="Contingencia"
                  checked={formik.values.color === ColorState.VIOLET}
                  onChange={(checked) =>
                    setFieldValue('color', checked ? ColorState.VIOLET : null)
                  }
                />
              </div>
            )}
            <FormikOnFieldChange<{
              client: ClientBusinessPartialModel | null;
            }>
              field="clientId"
              onChange={(client) => {
                setClientIdSelected(client);
                setFieldValue('authorizedId', null);
              }}
            />
            <FormikOnFieldChange<{
              sent_reservations_platform: boolean;
            }>
              field="sent_reservations_platform"
              onChange={(sent_reservations_platform) => {
                setFieldValue(
                  'sent_reservations_platform',
                  sent_reservations_platform
                );
                formik.values.not_sent_reservations_platform &&
                  sent_reservations_platform &&
                  setFieldValue('not_sent_reservations_platform', false);
              }}
            />
            <FormikOnFieldChange<{
              not_sent_reservations_platform: boolean;
            }>
              field="not_sent_reservations_platform"
              onChange={(value) => {
                setFieldValue('not_sent_reservations_platform', value);
                formik.values.sent_reservations_platform &&
                  value &&
                  setFieldValue('sent_reservations_platform', false);
              }}
            />
            <FormikOnFieldChange<{
              serviceType: string;
            }>
              field="serviceType"
              onChange={(value) => {
                setFieldValue('serviceType', value);
                if (value === 'Buses') {
                  setDisabledBuses(false);
                } else {
                  setDisabledBuses(true);
                  setFieldValue('bus_type_id', null);
                }
              }}
            />

            {filterByDateRange && (
              <div className={sectionClass}>
                <FilterCategoryTitle title="Periodo" />
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Desde">
                    <StyledDateTimePicker
                      value={formik.values.dateFrom}
                      onChange={(value) => setFieldValue('dateFrom', value)}
                    />
                  </FormField>
                  <FormField label="Hasta">
                    <StyledDateTimePicker
                      value={formik.values.dateTo}
                      onChange={(value) => setFieldValue('dateTo', value)}
                    />
                  </FormField>
                </div>
              </div>
            )}

            <div className={sectionClass}>
              <FilterCategoryTitle title="Reserva" />
              <div className="grid grid-cols-2 gap-3">
                {filterByClientType && (
                  <FormikSelect
                    name="client_type"
                    label="Tipo de cliente"
                    options={clientTypeOptions}
                  />
                )}

                {filterByClient && clientesQuery.data && (
                  <FormField
                    label="Cliente"
                    error={touched['clientId'] && errors['clientId']}
                  >
                    <StyledAutocomplete<number>
                      options={getClientOptions(formik.values.client_type)}
                      value={formik.values.clientId ?? null}
                      getOptionLabel={(option) =>
                        getClientLabel(option, formik.values.client_type)
                      }
                      onChange={(value) => setFieldValue('clientId', value)}
                      placeholder="Todos"
                    />
                  </FormField>
                )}

                {filterByServiceType && (
                  <FormikSelect
                    name="serviceType"
                    label="Tipo de servicio"
                    options={serviceTypeOptions}
                    className="col-span-2"
                  />
                )}

                {filterByBusType && (
                  <FormField label="Tipo de bus" className="col-span-2">
                    <div
                      className={tw(
                        'grid max-h-40 grid-cols-2 gap-2 overflow-y-auto rounded-lg border border-line bg-whiteLight p-3 dark:border-white/10 dark:bg-darkLight',
                        disabledBuses && 'opacity-60'
                      )}
                    >
                      {optionsBuses.map(({ id, name }) => (
                        <CheckboxField
                          key={id}
                          label={name}
                          disabled={disabledBuses}
                          checked={(formik.values.bus_type_id ?? []).includes(
                            id
                          )}
                          onChange={(checked) => {
                            const current = formik.values.bus_type_id ?? [];
                            setFieldValue(
                              'bus_type_id',
                              checked
                                ? [...current, id]
                                : current.filter((busId) => busId !== id)
                            );
                          }}
                        />
                      ))}
                    </div>
                  </FormField>
                )}

                {filterByStreet && <FormikInput name="street" label="Calle" />}
                {filterByLocality && (
                  <FormikInput name="locality" label="Localidad" />
                )}
                {filterByReservationId && (
                  <FormikInput
                    name="reservationId"
                    label="Reserva ID"
                    type="number"
                  />
                )}
                {filterByClientPhone && (
                  <FormikInput
                    name="client_phone"
                    label="Teléfono del cliente"
                    type="number"
                  />
                )}
                {filterByEventName && (
                  <FormikInput
                    name="event_name"
                    label="Nombre del evento"
                    className="col-span-2"
                  />
                )}

                <div className="col-span-2 grid grid-cols-2 gap-3 pt-1">
                  {filterByPriority && (
                    <FormikCheckbox name="hasPriority" label="Prioritario" />
                  )}
                  {filterByHasPlatformId && (
                    <FormikCheckbox
                      name="has_platform_id"
                      label="Sol. Plataforma"
                    />
                  )}
                  {filterByIsLltPais && (
                    <FormikCheckbox name="is_llt_pais" label="LLT-Pais" />
                  )}
                  {filterByFromApp && (
                    <FormikCheckbox name="from_app" label="Desde App" />
                  )}
                  {filterByIsEvent && (
                    <FormikCheckbox name="is_event" label="Evento" />
                  )}
                </div>

                {filterBySentPlatform && (
                  <FormField
                    label="Cargado en plataforma"
                    className="col-span-2"
                  >
                    <div className="flex gap-6">
                      <FormikCheckbox
                        name="sent_reservations_platform"
                        label="Si"
                      />
                      <FormikCheckbox
                        name="not_sent_reservations_platform"
                        label="No"
                      />
                    </div>
                  </FormField>
                )}
              </div>
            </div>

            {(filterByAuthorized ||
              filterByCostCenter ||
              filterBySubCostCenter ||
              filterBySector ||
              filterByBuyOrder ||
              filterByPlataformaId ||
              filterByState) && (
              <div className={sectionClass}>
                <FilterCategoryTitle title="Requerimientos" />
                <div className="grid grid-cols-2 gap-3">
                  {filterByAuthorized && (
                    <FormikSelect
                      name="authorized_id"
                      label="Autorizado"
                      options={authorizedOptions}
                      disabled={autorizadosQuery.data?.length === 0}
                      className="col-span-2"
                    />
                  )}
                  {filterByCostCenter && (
                    <FormikInput name="cost_center" label="Centro de costos" />
                  )}
                  {filterBySubCostCenter && (
                    <FormikInput
                      name="sub_cost_center"
                      label="Sub centro de costos"
                    />
                  )}
                  {filterBySector && (
                    <FormikInput name="sector" label="Sector" />
                  )}
                  {filterByBuyOrder && (
                    <FormikInput name="buy_order" label="Orden de compra" />
                  )}
                  {filterByPlataformaId && (
                    <FormikInput name="plataformaId" label="ID plataforma" />
                  )}
                  {filterByState && (
                    <FormikSelect
                      name="state"
                      label="Estado"
                      options={stateOptions}
                    />
                  )}
                </div>
              </div>
            )}

            {(filterByPassangerName || filterByPhone) && (
              <div className={sectionClass}>
                <FilterCategoryTitle title="Pasajero" />
                <div className="grid grid-cols-2 gap-3">
                  {filterByPassangerName && (
                    <FormikInput name="passangerName" label="Pasajero" />
                  )}
                  {filterByPhone && (
                    <FormikInput name="phone" label="Teléfono pasajero" />
                  )}
                </div>
              </div>
            )}

            {(filterByPreAssignedDriver ||
              filterByAssignedDriver ||
              filterByBusinessUnit ||
              filterByDriverWithCloseShift ||
              filterByDepartureTimeExceeded) && (
              <div className={sectionClass}>
                <FilterCategoryTitle title="Chofer" />
                <div className="flex flex-col gap-3">
                  {(driversQuery.data || businessQuery.data) &&
                    filterByPreAssignedDriver && (
                      <FormField
                        label="Chofer pre asignado"
                        error={
                          touched['pre_assigned_driver_id'] &&
                          errors['pre_assigned_driver_id']
                        }
                      >
                        <StyledAutocomplete<number>
                          options={(driversQuery.data ?? []).map((x) => x.id)}
                          value={formik.values.pre_assigned_driver_id ?? null}
                          getOptionLabel={(option) =>
                            typeof option === 'number'
                              ? getDriverName(option)
                              : ''
                          }
                          onChange={(value) =>
                            setFieldValue('pre_assigned_driver_id', value)
                          }
                          placeholder="Todos"
                        />
                      </FormField>
                    )}
                  {driversQuery.data && filterByAssignedDriver && (
                    <FormField
                      label="Chofer asignado"
                      error={
                        touched['assigned_driver_id'] &&
                        errors['assigned_driver_id']
                      }
                    >
                      <StyledAutocomplete<number>
                        options={(driversQuery.data ?? []).map((x) => x.id)}
                        value={formik.values.assigned_driver_id ?? null}
                        getOptionLabel={(option) =>
                          typeof option === 'number'
                            ? getDriverName(option)
                            : ''
                        }
                        onChange={(value) =>
                          setFieldValue('assigned_driver_id', value)
                        }
                        placeholder="Todos"
                      />
                    </FormField>
                  )}
                  {businessQuery.data &&
                    filterByAssignedDriver &&
                    filterByBusinessUnit && (
                      <FormField
                        label="Unidad de negocio"
                        error={
                          touched['business_unit'] && errors['business_unit']
                        }
                      >
                        <StyledAutocomplete<number>
                          options={(businessQuery.data ?? []).map((x) => x.id)}
                          value={
                            (formik.values.business_unit as unknown as
                              | number
                              | undefined) ?? null
                          }
                          getOptionLabel={(option) =>
                            (businessQuery.data ?? []).find(
                              (x) => x.id === option
                            )?.name ?? ''
                          }
                          onChange={(value) =>
                            setFieldValue('business_unit', value)
                          }
                          placeholder="Todas"
                        />
                      </FormField>
                    )}
                  {(filterByDriverWithCloseShift ||
                    filterByDepartureTimeExceeded) && (
                    <div className="flex flex-col gap-3 pt-1">
                      {filterByDriverWithCloseShift && (
                        <FormikCheckbox
                          name="driver_with_close_shift"
                          label="Chofer con turno cerrado"
                        />
                      )}
                      {filterByDepartureTimeExceeded && (
                        <FormikCheckbox
                          name="departure_time_exceeded"
                          label="Horario de salida superado"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {autoApply ? (
              <FormikAutoSubmit initialValues={initialValues} />
            ) : (
              <div className="col-span-1 flex justify-end pt-4">
                <Button onClick={submitForm} variant="contained">
                  Buscar
                </Button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ViajeTrasladoBaseFilters;
